import type { SortItem, SortEvent, SortAlgorithm } from '@/core/types';
import { EventRecorderImpl } from '@/core/EventRecorderImpl';

export class MergeSort implements SortAlgorithm {
  readonly name = 'Merge Sort';
  readonly category = 'comparison' as const;
  readonly stable = true;
  readonly inPlace = false;
  readonly adaptive = false;
  readonly best = 'O(n log n) - best case same as average';
  readonly average = 'O(n log n) - average case';
  readonly worst = 'O(n log n) - worst case identical to average';
  readonly space = 'O(n) - auxiliary array for merging';

  run(input: SortItem[]): SortEvent[] {
    const recorder = new EventRecorderImpl();
    const arr = input.map(item => ({ ...item }));
    this.mergeSort(arr, 0, arr.length - 1, recorder);
    recorder.record({ type: 'done', indices: arr.map((_, i) => i) });
    return recorder.events;
  }

  private mergeSort(arr: SortItem[], left: number, right: number, recorder: EventRecorderImpl): void {
    if (left >= right) return;
    const mid = Math.floor((left + right) / 2);
    recorder.record({ type: 'split', start: left, mid, end: right });
    this.mergeSort(arr, left, mid, recorder);
    this.mergeSort(arr, mid + 1, right, recorder);
    this.merge(arr, left, mid, right, recorder);
    recorder.record({ type: 'merge', start: left, mid, end: right });
  }

  private merge(arr: SortItem[], left: number, mid: number, right: number, recorder: EventRecorderImpl): void {
    const leftArr = arr.slice(left, mid + 1);
    const rightArr = arr.slice(mid + 1, right + 1);

    let i = 0, j = 0, k = left;
    while (i < leftArr.length && j < rightArr.length) {
      recorder.record({ type: 'compare', indices: [left + i, mid + 1 + j] });
      if (leftArr[i].value <= rightArr[j].value) {
        recorder.record({ type: 'overwrite', index: k, value: leftArr[i] });
        arr[k] = leftArr[i];
        i++;
      } else {
        recorder.record({ type: 'overwrite', index: k, value: rightArr[j] });
        arr[k] = rightArr[j];
        j++;
      }
      arr[k].currentIndex = k;
      k++;
    }

    while (i < leftArr.length) {
      recorder.record({ type: 'overwrite', index: k, value: leftArr[i] });
      arr[k] = leftArr[i];
      arr[k].currentIndex = k;
      i++; k++;
    }

    while (j < rightArr.length) {
      recorder.record({ type: 'overwrite', index: k, value: rightArr[j] });
      arr[k] = rightArr[j];
      arr[k].currentIndex = k;
      j++; k++;
    }
  }
}
